import React from "react";
import { Form, Input, Button, Flex, theme, type FormInstance, Select, Divider } from "antd";
import { CheckCircleFilled, UserOutlined, PhoneOutlined, MailOutlined, IdcardOutlined } from "@ant-design/icons";
import { SectionHeader, FieldLabel, inputStyle, SmartLoad } from "../../shared";
import type { UserData } from "../../PDFReader/types";

import type { ClientData } from "../types";
import openNotification from "../../../utils/openNotification";

interface ClientFormProps {
  form: FormInstance<ClientData>;
  onFinish: (values: ClientData) => void;
  isPending: boolean;
  buttonText: string;
  formId?: string;
}

const ClientForm: React.FC<ClientFormProps> = ({ form, onFinish, isPending, buttonText, formId = "client-form" }) => {
  const { token } = theme.useToken();
  const iStyle = inputStyle(token);

  const clientType = Form.useWatch("client_type", form);

  const handleDataExtracted = (data: UserData) => {
    console.log("handleDataExtracted", data);
    form.setFieldsValue({
      client_rfc: data.rfc_company,
      client_cp: data.cp ? Number(data.cp) : undefined,
      client_name: data.name,
      client_lastname_1: data.lastname_1,
      client_lastname_2: data.lastname_2,
      //client_tax_regime: data.tax_regime,
      // If Moral, set company name. If Fisica, set name (simplistic mapping)
      client_company_name: data.name_company,
    });
    openNotification("success", "Datos extraídos correctamente");
  };

  const handleFinish = (values: any) => {

    onFinish({
      ...values,
      client_cp: Number(values.client_cp),
      client_phone: String(values.client_phone),
    });
  };

  return (
    <Form
      id={formId}
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <div>
        <SmartLoad
          onDataExtracted={handleDataExtracted}
          description="Sube la Constancia de tu cliente para auto-completar sus datos fiscales."
        />
      </div>
      <Divider style={{ margin: "0 0 24px" }} />

      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Información del Cliente" />

        <Form.Item
          name="client_name"
          label={<FieldLabel text="Nombres" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Juan Carlos"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="client_lastname_1"
          label={<FieldLabel text="Apellido Paterno" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Pérez"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="client_lastname_2"
          label={<FieldLabel text="Apellido Materno" />}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Martínez"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="client_type"
          label={<FieldLabel text="Tipo de Cliente" />}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            placeholder="Seleccionar"
            style={iStyle}
            options={[
              { value: "Fisica", label: "Física" },
              { value: "Moral", label: "Moral" },
            ]}
          />
        </Form.Item>

        {clientType === "Moral" && (
          <Form.Item
            name="client_company_name"
            label={<FieldLabel text="Razón Social" />}
            rules={[{ required: true, message: "Campo requerido" }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              size="large"
              placeholder="Ej. Nombre de la empresa"
              style={iStyle}
            />
          </Form.Item>
        )}
      </Flex>

      <Divider style={{ margin: "0 0 24px" }} />

      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Información Fiscal" />

        <Form.Item
          name="client_rfc"
          label={<FieldLabel text="RFC" />}
          rules={[
            {
              len: clientType === "Fisica" ? 13 : 12,
              message: `El RFC para persona ${clientType === "Fisica" ? "física" : "moral"} debe tener ${clientType === "Fisica" ? 13 : 12} caracteres`
            },
            {
              pattern: /^[A-Z&Ñ]{3,4}[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[A-Z0-9]{3}$/,
              message: "Formato de RFC inválido"
            }
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<IdcardOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. PEMJ800101XX1"
            style={iStyle}
            onInput={(e) => (e.currentTarget.value = e.currentTarget.value.toUpperCase())}
          />
        </Form.Item>

        <Form.Item
          name="client_tax_regime"
          label={<FieldLabel text="Régimen Fiscal" />}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            placeholder="Ej. 605 - Persona física con actividad empresarial y profesional"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="client_cp"
          label={<FieldLabel text="Código Postal" />}
          rules={[
            { type: "string", len: 5, message: "El CP debe tener 5 dígitos", transform: (value) => value ? String(value) : value },
            { type: "string", pattern: /^[0-9]+$/, message: "Solo dígitos", transform: (value) => value ? String(value) : value }
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            placeholder="Ej. 11000"
            style={iStyle}
            maxLength={5}
          />
        </Form.Item>
      </Flex>

      <Divider style={{ margin: "0 0 24px" }} />

      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Contacto" />

        <Form.Item
          name="client_email"
          label={<FieldLabel text="Correo Electrónico" />}
          rules={[{ type: "email", message: "Correo inválido", required: true }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<MailOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="contacto@cliente.com"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="client_phone"
          label={<FieldLabel text="Teléfono" />}
          rules={[
            { required: true, message: "Campo requerido" },
            { type: "string", len: 10, message: "El teléfono debe tener 10 dígitos", transform: (value) => value ? String(value) : value },
            { type: "string", pattern: /^[0-9]+$/, message: "Solo dígitos", transform: (value) => value ? String(value) : value }
          ]}
          style={{ marginBottom: 12 }}
        >
          <Input
            size="large"
            prefix={<PhoneOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. 123 456 7890"
            style={iStyle}
            maxLength={10}
          />
        </Form.Item>
      </Flex>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={isPending}
        icon={<CheckCircleFilled />}
        block
        style={{
          height: 54,
          borderRadius: token.borderRadiusLG,
          fontWeight: 800,
          fontSize: 16,
          letterSpacing: "0.01em",
          boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
        }}
      >
        {buttonText}
      </Button>
    </Form>
  );
};

export default ClientForm;
